<template>
    <div>
        <div class="lending-criteria-container">
            <div class="lending-criteria-filter uk-grid uk-child-width-1-3@m">

                <div class="uk-form-controls">
                    <div>
                        Search lending criteria
                    </div>
                    <label class="kmc-input kmc-input-text">
                        <span class="sr-only">Search criteria</span>
                        <div class="prefix"><span uk-icon="search"></span></div>
                        <input type="text" v-model="searchCriteria"/>
                        <div class="border"></div>
                    </label>
                </div>


                <div class="uk-form-controls">
                    <div class="uk-display-block">
                        Product type
                    </div>
                    <div class="kmc-input kmc-select">
                        <label class="sr-only" for="selectedCriteriaTag">Select product type</label>
                        <select id="selectedCriteriaTag" v-model="selectedCriteriaTag">
                            <option v-for="option in criteriaOptions" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                        <div class="arrow"></div>
                    </div>
                </div>

            </div>

            <div class="lending-criteria-list-container">

                <div class="uk-flex uk-flex-nowrap uk-flex-gap uk-flex-between">
                    <div :class="'uk-margin-small'" v-html="resultsLabel"></div>
                    <div>
                        Sort by
                        <span tabindex="0" role="button"
                              :class="{ 'uk-button uk-button-text': orderBy === orderType.AZ }"
                              @click="setOrderBy(orderType.Popularity)">Popularity</span>
                        <span class="color-dark-grey">&nbsp;|&nbsp;</span>
                        <span tabindex="0" role="button" class="uk-margin-remove"
                              :class="{ 'uk-button uk-button-text': orderBy === orderType.Popularity }"
                              @click="setOrderBy(orderType.AZ)">A-Z</span>
                    </div>
                </div>


                <div class="uk-accordion lending-criteria-list"
                     uk-accordion="toggle: div.lending-criteria-title;duration:100">
                    <div v-for="criteria in lendingCriteriaFiltered" :key="criteria.Id" class="lending-criteria-row">
                        <div class="uk-padding-small bg-color-white uk-flex uk-flex-gap ">

                            <div class="uk-text-bold uk-width-expand lending-criteria-title" role="button" tabindex="0"
                                 v-html="criteriaTitleFormatted(criteria)" :tooltip="criteria.Title">
                            </div>

                            <button @click="addRemoveCriteria(criteria)" class="uk-button uk-button-text">
                                <span :uk-icon="Criteriaelected(criteria) ? 'trash' : 'plus-circle'"></span>
                                <span class="label uk-visible@m">{{
                                        Criteriaelected(criteria) ? 'Clear' :
                                            'Save to my criteria'
                                    }}</span>
                            </button>
                        </div>

                        <div class="uk-accordion-content">
                            <div v-html="criteriaContentFormatted(criteria)"></div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="lending-criteria-toolbar-container uk-padding-top">
                
                <div class="lending-criteria-toolbar">
                    <div class="uk-flex uk-flex-middle uk-flex-gap uk-text-lead uk-text-bold">
                        <span uk-icon="plus-one"></span>
                        <div v-html="lendingPolicyLabel"></div>
                    </div>

                    <div class="selected-critera-list">

                        <div v-if="currentlySelectedCriteria.length > 0" v-for="criteria in currentlySelectedCriteria"
                             :key="criteria.Id"
                             class="selected-critera-list-item uk-flex uk-flex-middle uk-flex-gap uk-text-bold uk-flex-between">

                            <div v-html="criteria.Title" :tooltip="criteria.Title"></div>
                            <button class="uk-button uk-button-text"><span uk-icon="trash"
                                                                           @click="addRemoveCriteria(criteria)"></span><span
                                class="sr-only">Remove</span></button>
                        </div>

                        <div v-else
                             class="selected-critera-list-item selected-critera-list-item-empty uk-text-center color-dark-grey">
                            no criteria selected
                        </div>
                    </div>

                    <div class="uk-padding-small uk-border" v-if="currentlySelectedCriteria.length > 0">
                        <div class="uk-flex uk-flex-wrap uk-flex-between uk-flex-gap">
                            <button class="uk-button uk-button-outline uk-button-primary uk-margin-remove"
                                    @click="downloadPDF">Download
                                as PDF
                            </button>
                            <button class="uk-button uk-button-text" @click="clearSelection">
                                Clear all <span uk-icon="trash"></span></button>
                        </div>
                    </div>
                </div>

                
            </div>
            <div class="lending-criteria-secondary-toolbar-container">
                <div class="uk-margin uk-text-center">
                    <a v-if="productGuide"  class="uk-button uk-button-default uk-button-secondary uk-button-outline secondary"
                        :href="productGuide.href"
                        :target="productGuide.target"
                    >
                        <span class="uk-sr-only" v-text="productGuide.text"></span>
                        <span uk-icon="download"></span>
                        <span v-text="productGuide.text"></span>
                    </a>
                </div>
                <div class="lending-criteria-secondary-toolbar" v-if="tips">
                    <div v-html="tips"></div>
                </div>
            </div>
        </div>

    </div>

</template>
<script>

import {ref, computed, watch} from 'vue';
import {KmcInputText, KmcInputDropdown} from "../components";
import {getPDF} from "./PDFGenerator.js";


export default {
    name: 'LendingCriteriaWidget',
    components: {KmcInputDropdown, KmcInputText},
    props: {
        LendingCriteria: {
            type: Array,
            required: true
        },
        LendingCriteriaTags: {
            type: Array,
            required: true
        },
        ExtraFooterContent: {
            type: String,
            required: true,
        },
        ProductSpecificOptions: {
            type: Object,
            required: true,
        }
    },
    setup(props, context) {

        const criteriaOptions = props.LendingCriteriaTags.map((tag) => {
            return {
                label: tag['Title'],
                value: tag['Id']
            }
        });

        const lendingCriteraById = new Map();
        props.LendingCriteria.forEach((c) => lendingCriteraById.set(c.Id, c));

        const searchCriteria = ref('');
        const selectedCriteriaTag = ref(criteriaOptions[0].value);

        const selectedCriteriaByCriteriaTag = ref(new Map());

        const orderType = {AZ: 'az', Popularity: 'popularity'};

        const orderBy = ref(orderType.Popularity);

        criteriaOptions.forEach((option) => {
            selectedCriteriaByCriteriaTag.value.set(option.value, new Set());
        });

        const lendingCriteriaFiltered = computed(() => {
            const regex = new RegExp(searchCriteria.value, 'i');
            const selectedTag = selectedCriteriaTag.value;
            const searchQuery = searchCriteria.value.toLowerCase();

            let result = props.LendingCriteria.reduce((acc, criteria) => {
                if (!criteria.lendingcriteriatags.includes(selectedTag)) return acc;

                let rank = 0;
                const titleLower = criteria.Title.toLowerCase();
                const contentLower = criteria.Content.toLowerCase();

                if (titleLower.startsWith(searchQuery)) {
                    rank = 3; // Highest priority
                } else if (regex.test(titleLower)) {
                    rank = 2; // Second priority
                } else if (regex.test(contentLower)) {
                    rank = 1; // Lowest priority
                }

                if (rank > 0) {
                    acc.push({...criteria, rank});
                }
                return acc;
            }, []);


            if (orderBy.value === orderType.Popularity) {
                result.sort((a, b) => b.Popularity - a.Popularity || b.rank - a.rank || a.Title.localeCompare(b.Title));
            } else {
                result.sort((a, b) => a.Title.localeCompare(b.Title) || b.rank - a.rank);
            }


            return result;
        });

        const currentSet = () => {
            return selectedCriteriaByCriteriaTag.value.get(selectedCriteriaTag.value);
        }

        const addRemoveCriteria = (criteria) => {
            let current = currentSet();
            if (current.has(criteria.Id)) {
                current.delete(criteria.Id);
            } else {
                current.add(criteria.Id);
            }
        }


        const Criteriaelected = (criteria) => {
            return currentSet().has(criteria.Id);
        }

        const filterApplied = computed(() => {
            return searchCriteria.value !== ''
        });

        const hasSelectedCriteria = computed(() => {
            return !!currentlySelectedCriteria.value.length;
        })

        const resultsLabel = computed(() => {
            if (filterApplied.value && lendingCriteriaFiltered.value.length > 0) {
                return `Showing ${lendingCriteriaFiltered.value.length} criteria for <b>${searchCriteria.value}</b>`;
            } else if (filterApplied.value && lendingCriteriaFiltered.value.length < 1) {
                return `No criteria found for <b>${searchCriteria.value}</b>`;
            } else {
                return `Showing all ${lendingCriteriaFiltered.value.length} criteria`;
            }
        });

        const lendingPolicyLabel = computed(() => {
            const selectedCriteriaCount = currentlySelectedCriteria.value.length;
            let label = 'My saved criteria';

            if (selectedCriteriaCount > 0) {
                label += ` (${selectedCriteriaCount})`;
            }
            return label;
        })
        
        const productSpecificOption = function(optionKey) {
            var option = props.ProductSpecificOptions[selectedCriteriaTag.value];
            if(!option || !Object.hasOwn(option, optionKey)) return null;
            return option[optionKey];
        }
        
        const productGuide = computed(() => {
            return productSpecificOption('ProductGuide');
        })
        
        const tips = computed(() => {
            return productSpecificOption('Tips');
        })
        
        const currentlySelectedCriteria = computed(() => {
            let selected = currentSet();
            const entries = [];
            for (let id of selected) {
                entries.push(lendingCriteraById.get(id));
            }
            return entries.sort((a, b) => a.Title.localeCompare(b.Title));
        });

        const clearSelection = () => {
            selectedCriteriaByCriteriaTag.value.get(selectedCriteriaTag.value).clear();
        }

        const downloadPDF = () => {
            const selectedCriteria = currentlySelectedCriteria.value;
            const isResidential = selectedCriteriaTag.value === criteriaOptions[0].value;
            const document = getPDF(selectedCriteria, isResidential, props.ExtraFooterContent);
            document.download('KMC Lending Policy.pdf');
        }


        const setOrderBy = (type) => {
            orderBy.value = type;
        }


        const highlightContent = (needle, content) => {
            if (!needle || !content) return content;
            
            const domContent = domParser.parseFromString(content, 'text/html');

            domHighlightText(domContent, needle);
            
            return domContent.body.innerHTML;
        }

        const domParser = new DOMParser();

        function domHighlightText(rootElement, searchTerm) {
            if (!searchTerm.trim()) return;

            const regex = new RegExp(searchTerm, 'gi');
            
            rootElement.childNodes.forEach(node => {
                if (node.nodeType === Node.TEXT_NODE) {
                    const html = node.nodeValue.replace(regex, match => `<mark>${match}</mark>`);
                    const temp = document.createElement('span'); // Temporary wrapper
                    temp.innerHTML = html;
                    node.replaceWith(...temp.childNodes);
                } else if (node.nodeType === Node.ELEMENT_NODE) {
                    domHighlightText(node, searchTerm);
                }
            });
        }
        
        const criteriaTitleFormatted = (criteria) => {
            return highlightContent(searchCriteria.value, criteria.Title);
        }

        const criteriaContentFormatted = (criteria) => {
            return highlightContent(searchCriteria.value, criteria.Content);
        }

        return {
            searchCriteria,
            selectedCriteriaTag,
            criteriaOptions,
            lendingCriteriaFiltered,
            addRemoveCriteria,
            Criteriaelected,
            selectedCriteriaByCriteriaTag,
            resultsLabel,
            lendingPolicyLabel,
            currentlySelectedCriteria,
            clearSelection,
            downloadPDF,
            hasSelectedCriteria,
            orderBy,
            orderType,
            setOrderBy,
            criteriaTitleFormatted,
            criteriaContentFormatted,
            tips,
            productGuide
        }
    },
    mounted() {
        const handleKeydown = (event) => {
            const role = event.target.getAttribute('role');
            const key = event.key;

            if (role === 'button' && (key === 'Enter' || key === ' ')) {
                event.preventDefault();
                event.target.click();
            }
        };

        this.$el.addEventListener('keydown', handleKeydown);
    }
}
</script>

<style lang="scss">
.lending-criteria {
    &-container {
        display: grid;
        position: relative;
        grid-template-columns: minmax(400px, auto) minmax(300px, 350px);
        grid-template-rows: auto;
        grid-template-areas:
            "filter filter"
            "list toolbar"
            "list secondary-toolbar";
        gap: 20px;

        @media screen and (max-width: 960px) {
            grid-template-columns: auto;
            grid-template-areas:
                "secondary-toolbar"
                "filter"
                "toolbar"
                "list";
        }
    }

    &-filter {
        grid-area: filter;
    }

    
    
    &-toolbar-container {
        grid-area: toolbar;
    }

    &-secondary-toolbar-container {
        grid-area: secondary-toolbar;
    }
    
    &-secondary-toolbar {
        background-color: white;
        border: 1px solid var(--color-mid-grey);
        border-radius: 8px;
        margin-bottom: 10px;
        overflow: clip;
        padding: 20px;
    }
    
    &-toolbar {
        background-color: white;
        border: 1px solid var(--color-mid-grey);
        border-radius: 8px;
        margin-bottom: 10px;
        overflow: clip;

        @media screen and (min-width: 960px) {
            position: sticky;
            top: 100px;
        }

        .selected-critera-list {
            height: 100%;
            max-height: 60vh;
            overflow-y: auto;

            &-item {
                padding: 20px;

                & > div {
                    display: -webkit-box;
                    -webkit-box-orient: vertical;
                    -webkit-line-clamp: 3;
                    overflow: hidden;
                }

                &:not(:last-of-type) {
                    border-bottom: 1px solid var(--color-mid-grey);
                }
            }
        }

        & > :first-child {
            padding: 30px 20px;
            border-bottom: 1px solid var(--color-mid-grey);
        }

        & > :last-child {
            padding: 20px;
            border-top: 1px solid var(--color-mid-grey);
        }
    }

    &-list-container {
        grid-area: list;
        grid-row-end: none;
    }

    &-title {
        position: relative;
        padding-left: 30px;

        &::before {
            content: "";
            position: absolute;
            top: 0;
            left: 0;
            bottom: 0;
            width: 20px;
            background-repeat: no-repeat;
            background-position: center;
            background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20'%3E%3Cpolyline fill='none' stroke='%23000' stroke-width='1.03' points='16 7 10 13 4 7'%3E%3C/polyline%3E%3C/svg%3E");
            transition: all 100ms ease-in-out;
        }

        &[aria-expanded="true"] {
            &::before {
                transform: rotate(180deg);
            }
        }
    }
}


.table {
    width: 100%;
    margin-bottom: 1rem;
    background-color: transparent;
    border-collapse: collapse;

    &.k-table {
        border: 1px solid #dee2e6;

        thead {
            &.thead-dark {
                th {
                    color: #fff;
                    background-color: #343a40;
                    border-color: #454d55;
                    padding: 0.75rem;
                    vertical-align: top;
                    border-top: 1px solid #dee2e6;
                    border-bottom: 2px solid #dee2e6;
                    text-align: left;
                }
            }
        }

        tbody {
            tr {
                &:nth-child(odd) {
                    background-color: #f2f2f2;
                }

                &:nth-child(even) {
                    background-color: #ffffff;
                }

                td {
                    padding: 0.75rem;
                    vertical-align: top;
                    border-top: 1px solid #dee2e6;
                    text-align: left;
                }
            }
        }
    }


}
</style>